import React, { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

export default function ReviewSlider() {
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadReviews() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/reviews");
        if (res.ok) {
          const data = await res.json();
          setReviews(data);
        }
      } catch (err) {
        console.error("Fejl ved hentning af anmeldelser:", err);
      } finally {
        setLoading(false);
      }
    }
    loadReviews();
  }, []);

  // Autoplay
  useEffect(() => {
    if (reviews.length === 0) return;
    const interval = setInterval(() => {
      handleNext();
    }, 6000);
    return () => clearInterval(interval);
  }, [reviews, currentIndex]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? Math.max(0, reviews.length - 1) : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === Math.max(0, reviews.length - 1) ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#C2A679] border-t-transparent"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Slider Container */}
      <div className="overflow-hidden">
        {/* Grid-baseret visning, der skifter afhængigt af skærmstørrelse */}
        {/* For at gøre det til en rigtig slider, viser vi et udsnit af listen baseret på skærmbredde */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Vi viser 3 anmeldelser ad gangen på desktop, 2 på tablet, 1 på mobil */}
          {/* Vi roterer elementerne i arrayet baseret på currentIndex for at skabe en flydende karrusel */}
          {[...Array(3)].map((_, i) => {
            const reviewIndex = (currentIndex + i) % reviews.length;
            const review = reviews[reviewIndex];

            // Skjul 3. kort på tablet, og 2. + 3. kort på mobil
            const isHiddenOnTablet = i === 2 ? "hidden lg:block" : "";
            const isHiddenOnMobile = i === 1 ? "hidden md:block" : "";

            if (!review) return null;

            return (
              <div
                key={review.id + "-" + i}
                className={`flex flex-col justify-between rounded-none border border-neutral-200/80 bg-white p-6 shadow-lg transition-all duration-300 hover:border-neutral-400 ${isHiddenOnTablet} ${isHiddenOnMobile}`}
              >
                <div>
                  {/* Stjerner */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating || 5)].map((_, starIdx) => (
                      <Star key={starIdx} className="h-5 w-5 fill-neutral-900 text-neutral-900" />
                    ))}
                  </div>

                  {/* Citat-ikon */}
                  <Quote className="h-8 w-8 text-neutral-900/10 mb-2" />

                  {/* Anmeldelsestekst */}
                  <p className="text-sm text-neutral-600 italic leading-relaxed mb-6">
                    "{review.text?.da || review.text}"
                  </p>
                </div>

                {/* Forfatter & Projekttype */}
                <div className="border-t border-neutral-100 pt-4">
                  <h4 className="text-sm font-bold text-neutral-900">{review.author}</h4>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mt-1">
                    {review.project_type?.da || review.project_type}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Pile */}
      {reviews.length > 1 && (
        <div className="mt-8 flex justify-center gap-4">
          <button
            onClick={handlePrev}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-950 transition-colors hover:border-neutral-400 focus:outline-none active:scale-95 shadow-sm"
            aria-label="Forrige anmeldelse"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={handleNext}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-950 transition-colors hover:border-neutral-400 focus:outline-none active:scale-95 shadow-sm"
            aria-label="Næste anmeldelse"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
